interface IArrayToObject {
  arr: any[];
  property: string | number;
  defaultValue?: string | null;
}

/**
 *
 * @param arr - the array
 * @param property - the property or index you would like to use
 * @param defaultValue - default value from object array
 * @description Transforms any array passed into a mapped object by the object property or index
 */
export function ArrayToObject({
  arr,
  property,
  defaultValue = null,
}: IArrayToObject): Record<string, any> {
  return arr.reduce((acc, current) => {
    return {
      ...acc,
      [current[property]]: defaultValue ? current[defaultValue] : null,
    };
  }, {});
}

interface IFlattenArrays {
  arrs: [any[]];
}

/**
 *
 * @param arrs - arrays nested together
 */

export function FlattenArrays({ arrs }: IFlattenArrays): any[] {
  return arrs.flat();
}

export function merge(left: Array<any>, right: Array<any>, property: string) {
  const sortedArr = []; // the sorted items will go here
  while (left.length && right.length) {
    // Insert the smallest item into sortedArr
    if (left[0][property] < right[0][property]) {
      sortedArr.push(left.shift());
    } else {
      sortedArr.push(right.shift());
    }
  }
  // Use spread operators to create a new array, combining the three arrays
  return [...sortedArr, ...left, ...right];
}

export function mergeSort(arr: Array<any>, property: string) {
  // Base case
  if (arr.length <= 1) return arr;
  const mid = Math.floor(arr.length / 2);
  // Recursive calls
  const left: Array<any> = mergeSort(arr.slice(0, mid), property);
  const right: Array<any> = mergeSort(arr.slice(mid), property);
  return merge(left, right, property);
}

export function formatDate(date: number | Date) {
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  })
    .format(date)
    .replace(',', '');
}

export function formatTransactionDate(date: Date) {
  return new Intl.DateTimeFormat('en-UK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  })
    .format(new Date(date))
    .replaceAll(',', '');
}

export function formatMobileTransactionDate(recordDate) {
  const date = new Date(recordDate);
  const hh = ('0' + date.getHours()).slice(-2);
  const mm = ('0' + date.getMinutes()).slice(-2);
  const ss = ('0' + date.getSeconds()).slice(-2);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  };
  return date.toLocaleDateString('en-GB', options) + `-${hh}:${mm}:${ss}`;
}

export function chunk(arr: Array<any>, chunkSize: number) {
  const R = [];
  for (let i = 0; i < arr.length; i += chunkSize)
    R.push(arr.slice(i, i + chunkSize));
  return R;
}

export function splitCookieByKey(cookieString: string, key: string) {
  const value = `; ${cookieString}`;
  const parts = value.split(`; ${key}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export function parseJwt(
  token: string,
): Record<string, unknown> & { exp?: number } {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

export const generateTranslationKey = (input: string): string => {
  if (!input) return;
  return input
    ?.trim()
    ?.replace(/([a-z])([A-Z])/g, '$1-$2')
    ?.replace(/[^a-zA-Z0-9\s-.]/g, '')
    ?.replace(/\s+/g, '-')
    ?.toLowerCase();
};

export const capitalizeString = (input: string): string => {
  return String(input).charAt(0).toUpperCase() + String(input).slice(1);
};

export function bitwiseCheck(num: number, n: number): number | boolean {
  return (num & n) === n;
}

export function cleanJsonString(input: string): string {
  if (!input) return input;

  let cleaned = input
    .replace(/^\uFEFF/, '')
    .replace(/<!--\s*(StartFragment|EndFragment)\s*-->/gi, '')
    .replace(/^\s*Version:\d+\.\d+\s*/i, '')
    .replace(/^\s*StartHTML:\d+\s*/i, '')
    .replace(/^\s*EndHTML:\d+\s*/i, '')
    .replace(/^\s*StartFragment:\d+\s*/i, '')
    .replace(/^\s*EndFragment:\d+\s*/i, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();

  const firstJsonObjectIndex = cleaned.search(/[{[]/);
  const lastObjectIndex = cleaned.lastIndexOf('}');
  const lastArrayIndex = cleaned.lastIndexOf(']');
  const lastJsonIndex = Math.max(lastObjectIndex, lastArrayIndex);

  if (
    firstJsonObjectIndex !== -1 &&
    lastJsonIndex !== -1 &&
    lastJsonIndex > firstJsonObjectIndex
  ) {
    cleaned = cleaned.slice(firstJsonObjectIndex, lastJsonIndex + 1);
  }
  return cleaned;
}

export function toPascalCase(string: string) {
  return `${string}`
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+(.)(\w*)/g, ($1, $2, $3) => `${$2.toUpperCase() + $3}`)
    .replace(/\w/, (s) => s.toUpperCase());
}